import { useState, type ReactNode } from 'react';
import type { AllocationDetailById } from '@billda/api';
import {
  Accordion,
  AccordionItem,
  Button,
  CancelIcon,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  MoreVertIcon,
  Stack,
  Text,
} from '@billda/design-system';
import type { AllocationStatus } from '../../../../api/sitesApi';
import { formatCurrency } from '../../../../../../shared/utils/currency';
import {
  formatDate,
  formatDateTime,
} from '../../../../../../shared/utils/dateFormatting';
import {
  formatQuantity,
  formatUnitRate,
} from '../../../../../../shared/utils/quantity';
import { AllocationStatusBadge } from '../../atoms/AllocationStatusBadge';
import { InfoRow } from '../../atoms/InfoRow';
import { PagedActivityTimeline } from '../../molecules/PagedActivityTimeline';
import type { UseAllocationEventsTimelineReturn } from '../../../../hooks/useAllocationEventsTimeline';

type Booking = AllocationDetailById;

function formatClaimQuantity(quantity: number, unitLabel: string) {
  const label =
    quantity === 1 || unitLabel.endsWith('s') ? unitLabel : `${unitLabel}s`;
  return formatQuantity(quantity, label);
}

interface BookingSectionProps {
  title: string;
  children: ReactNode;
  testId?: string;
}

export function BookingSection({
  title,
  children,
  testId,
}: BookingSectionProps) {
  return (
    <section
      data-testid={testId}
      className="border-t border-border-default py-5"
    >
      <Text variant="body2" className="mb-1.75 font-semibold">
        {title}
      </Text>
      {children}
    </section>
  );
}

interface BookingHeaderProps {
  booking: Booking;
  onCancel?: () => void;
  cancellationLocked: boolean;
}

export function BookingHeader({
  booking,
  onCancel,
  cancellationLocked,
}: BookingHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const title = booking.isVariation
    ? (booking.variation?.workTypeDisplayName ?? 'Variation')
    : (booking.labour?.name ?? 'Labour booking');
  const hasOutcome = ['Approved', 'Rejected', 'Cancelled'].includes(
    booking.status
  );

  return (
    <div className="pb-5">
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <div className="min-w-0 flex-1">
          <Text variant="h6" className="leading-tight">
            {title}
          </Text>
          {booking.isVariation && booking.variation?.description && (
            <Text
              variant="body2"
              color="secondary"
              className="mt-1.5 leading-normal"
            >
              {booking.variation.description}
            </Text>
          )}
          <Text
            variant={booking.isVariation ? 'caption' : 'body2'}
            color="secondary"
            className="mt-1.5 block leading-normal"
          >
            {booking.worker.name} · {booking.plot.siteName} ·{' '}
            <span className="whitespace-nowrap">
              Plot {booking.plot.plotReference}
            </span>
          </Text>
        </div>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {!hasOutcome && (
            <AllocationStatusBadge
              status={booking.status as AllocationStatus}
            />
          )}
          {onCancel && (
            <Menu open={menuOpen} onOpenChange={setMenuOpen}>
              <MenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  aria-label="Booking actions"
                  data-testid="booking-actions-menu-button"
                >
                  <MoreVertIcon className="size-4" aria-hidden="true" />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem
                  tone="destructive"
                  onSelect={() => {
                    setMenuOpen(false);
                    onCancel();
                  }}
                >
                  <CancelIcon className="size-4" aria-hidden="true" />
                  Cancel booking
                </MenuItem>
              </MenuContent>
            </Menu>
          )}
        </Stack>
      </Stack>
      {cancellationLocked && (
        <Text variant="caption" color="secondary" className="mt-1.5 block">
          Included in a submitted pot. Changes are locked.
        </Text>
      )}
    </div>
  );
}

export function BookingDecisionOutcome({ booking }: { booking: Booking }) {
  if (!['Approved', 'Rejected', 'Cancelled'].includes(booking.status)) {
    return null;
  }

  const isApproved = booking.status === 'Approved';
  const isRejected = booking.status === 'Rejected';
  const title = isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Cancelled';
  const actor =
    isRejected || isApproved
      ? booking.review?.reviewedByName
      : booking.cancelledByName;
  const timestamp =
    isRejected || isApproved ? booking.review?.reviewedAt : booking.cancelledAt;
  const notes = isRejected
    ? booking.review?.rejectionNotes
    : isApproved
      ? booking.review?.rejectionNotes
      : booking.cancellationNotes;
  const summary = isRejected
    ? booking.review?.rejectionReasonDisplayName
    : booking.status === 'Cancelled'
      ? booking.quantityClaimed != null && booking.unitLabel
        ? `${formatClaimQuantity(booking.quantityClaimed, booking.unitLabel)} returned`
        : `${formatCurrency(booking.amountClaimed)} returned`
      : null;

  return (
    <BookingSection title="Decision" testId="booking-decision-outcome">
      <div className="min-w-0">
        <Stack
          direction="row"
          alignItems="baseline"
          className="flex-wrap gap-x-2 gap-y-0.5"
        >
          <Text
            variant="body2"
            className={`font-semibold ${
              isApproved
                ? 'text-content-success'
                : isRejected
                  ? 'text-content-error'
                  : 'text-content-primary'
            }`}
          >
            {title}
          </Text>
          {(actor || timestamp) && (
            <Text variant="caption" color="secondary">
              {[actor, timestamp ? formatDateTime(timestamp) : null]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          )}
        </Stack>
        {summary && (
          <Text variant="body2" className="mt-0.5 font-semibold">
            {summary}
          </Text>
        )}
        {notes && (
          <Text
            variant="body2"
            color="secondary"
            className="mt-0.25 leading-normal"
          >
            {notes}
          </Text>
        )}
      </div>
    </BookingSection>
  );
}

function FixedLabourClaim({ booking }: { booking: Booking }) {
  return (
    <div data-testid="fixed-labour-claim">
      <Text variant="body2" color="secondary">
        Fixed-price labour
      </Text>
      <Text variant="h5" className="mt-0.5 font-semibold">
        {formatCurrency(booking.amountClaimed)}
      </Text>
      {booking.labour && booking.labour.availableAmount > 0 && (
        <Text variant="caption" color="secondary">
          {formatCurrency(booking.labour.availableAmount)} remaining on item
        </Text>
      )}
    </div>
  );
}

function QuantityLabourClaim({ booking }: { booking: Booking }) {
  const quantity = booking.quantityClaimed ?? 0;
  const unitLabel = booking.unitLabel ?? booking.labour?.unitLabel ?? 'unit';
  const unitRate = booking.unitRate ?? booking.labour?.unitRate ?? 0;
  const remainingQuantity = booking.labour?.remainingQuantity;

  return (
    <div data-testid="quantity-labour-claim">
      <Text variant="body2" color="secondary">
        Quantity claim
      </Text>
      <Text variant="h6" className="mt-0.5 font-semibold">
        {formatClaimQuantity(quantity, unitLabel)} ×{' '}
        {formatUnitRate(unitRate, unitLabel)}
      </Text>
      <Text variant="h5" className="mt-1.5 font-semibold">
        {formatCurrency(booking.amountClaimed)}
      </Text>
      {remainingQuantity != null && (
        <Text variant="caption" color="secondary" className="mt-0.5 block">
          Remaining on item: {formatClaimQuantity(remainingQuantity, unitLabel)}{' '}
          · {formatCurrency(remainingQuantity * unitRate)}
        </Text>
      )}
    </div>
  );
}

function VariationClaim({ booking }: { booking: Booking }) {
  return (
    <div data-testid="variation-claim">
      <Text variant="body2" color="secondary">
        Agreed variation
      </Text>
      <Text variant="h5" className="mt-0.5 font-semibold">
        {formatCurrency(booking.amountClaimed)}
      </Text>
    </div>
  );
}

export function BookingClaimSection({ booking }: { booking: Booking }) {
  const isQuantity =
    booking.quantityClaimed != null &&
    (booking.unitRate != null || booking.labour?.unitRate != null);

  return (
    <BookingSection title="Claim" testId="booking-claim-section">
      {booking.isVariation ? (
        <VariationClaim booking={booking} />
      ) : isQuantity ? (
        <QuantityLabourClaim booking={booking} />
      ) : (
        <FixedLabourClaim booking={booking} />
      )}
    </BookingSection>
  );
}

interface BookingEvidenceSectionProps {
  booking: Booking;
  children: ReactNode;
}

export function BookingEvidenceSection({
  booking,
  children,
}: BookingEvidenceSectionProps) {
  const notes = booking.isVariation
    ? booking.variation?.notes
    : booking.workNotes;

  return (
    <BookingSection title="Evidence" testId="booking-evidence-section">
      <div className="mb-2.25">
        <Text variant="body2" className="whitespace-pre-wrap">
          {notes || 'No work notes added'}
        </Text>
      </div>
      <Text variant="body2" color="secondary" className="mb-2">
        Photos
      </Text>
      {children}
    </BookingSection>
  );
}

interface BookingContextSectionProps {
  booking: Booking;
  referenceControl?: ReactNode;
}

export function BookingContextSection({
  booking,
  referenceControl,
}: BookingContextSectionProps) {
  return (
    <BookingSection title="Details" testId="booking-context-section">
      <InfoRow label="Site" testId="location-site">
        {booking.plot.siteName}
      </InfoRow>
      <InfoRow label="Plot" testId="location-plot">
        {booking.plot.plotName || booking.plot.plotReference}
      </InfoRow>
      <InfoRow label="Plot number" testId="location-plot-number">
        {booking.plot.plotReference}
      </InfoRow>
      {booking.bookedByName && (
        <InfoRow label="Booked by" testId="booked-by">
          {booking.bookedByName}
        </InfoRow>
      )}
      <InfoRow label="Work date" testId="work-date">
        {formatDate(booking.submittedAt)}
      </InfoRow>
      {booking.uniqueReference && (
        <InfoRow label="Reference" testId="unique-reference">
          {referenceControl ?? booking.uniqueReference}
        </InfoRow>
      )}
      {booking.pot && (
        <InfoRow label="Payment pot" testId="payment-pot">
          {booking.pot.display}
        </InfoRow>
      )}
    </BookingSection>
  );
}

export function BookingActivitySection({
  timeline,
}: {
  timeline: UseAllocationEventsTimelineReturn;
}) {
  return (
    <Accordion
      className="rounded-none border-0 border-t border-border-default bg-transparent shadow-none"
      multiple={false}
    >
      <AccordionItem
        value="booking-activity"
        title={
          <Text variant="body2" className="font-semibold">
            Activity
            {timeline.totalCount > 0 ? ` (${timeline.totalCount})` : ''}
          </Text>
        }
        className="border-0"
      >
        <div className="px-0 pt-0">
          <PagedActivityTimeline
            events={timeline.events}
            totalCount={timeline.totalCount}
            hasMore={timeline.hasMore}
            isLoading={timeline.isLoading}
            isLoadingMore={timeline.isLoadingMore}
            loadMore={timeline.loadMore}
          />
        </div>
      </AccordionItem>
    </Accordion>
  );
}
